import { z } from 'zod'
import { ValidationRule } from '@onedv-portal/shared'

const EMPTY_FIELD_ERROR = `Pflichtfeld darf nicht leer sein`

type RuleMessage = string | ((params: Record<string, unknown>) => string)

const RULE_MESSAGES: Record<ValidationRule, RuleMessage> = {
  [ValidationRule.FieldRequired]: EMPTY_FIELD_ERROR,
  [ValidationRule.BeforeDate]: (p) => `Das Datum muss vor dem ${p['date']} liegen.`,
  [ValidationRule.FutureDate]: 'Datum muss in der Zukunft liegen',
  [ValidationRule.PriceDateValid]: 'Das Vermarktungsentgelt ist älter als 48 Stunden. Bitte neu berechnen.',
  [ValidationRule.PriceInputsChanged]:
    'Preisrelevante Angaben wurden geändert. Bitte das Vermarktungsentgelt neu berechnen.',
  [ValidationRule.MarketLocationXor]:
    'Es muss entweder eine Marktlokations-ID oder eine Projekt-ID Netzbetreiber angegeben werden, aber nicht beides.',
  [ValidationRule.MarketLocationIdDuplicate]: 'Die Marktlokations-ID muss eindeutig sein.',
  [ValidationRule.ProjectIdGridOperatorDuplicate]: 'Die Projekt-ID Netzbetreiber muss eindeutig sein.',
  [ValidationRule.MarketLocationSingle]: 'Es muss genau eine Marktlokation angegeben werden.',
  [ValidationRule.MaloProjectIdRequired]: 'Bitte MaLo-ID oder Projekt-ID angeben.',
  [ValidationRule.MaloProjectIdMismatch]: 'Zugewiesene ID wurde geändert oder gelöscht.',
  [ValidationRule.CommissioningDateRequired]: 'Bitte geplantes Inbetriebnahmedatum oder Inbetriebnahmedatum angeben.',
  [ValidationRule.CommissioningDateOperationStatus]: 'Entspricht nicht dem Betriebsstatus Ihrer Anlage',
  [ValidationRule.ContractEndAfterStart]: 'Datum muss nach dem DV-Start liegen',
  [ValidationRule.ContractWithinFundingPeriod]: 'EEG-Förderung endet während der Vertragslaufzeit',
  [ValidationRule.VatIdOrTaxNumberRequired]: 'Umsatzsteuer-ID und/oder Steuernummer angeben.',
  [ValidationRule.BillingShareRequired]: 'Aufteilungsschlüssel ist erforderlich wenn Fix ausgewählt ist',
  [ValidationRule.AcerCodeRequired]: 'ACER-Code ist erforderlich, wenn die REMIT-Meldepflicht besteht.',
  [ValidationRule.MastrNumberFormat]: 'Die Eingabe muss mit „SEE“ beginnen, gefolgt von 12 Ziffern.',
  [ValidationRule.MastrNumberDuplicate]: 'Diese Marktstammdatenregister-Nummer wird bereits verwendet.',
  [ValidationRule.EegPlantKeyFormat]: 'Der EEG-Anlagenschlüssel muss mit „E" beginnen, gefolgt von 32 Zeichen.',
  [ValidationRule.GeoInputFormat]: 'Die Eingabe muss 3 bis 14 Nachkommastellen haben.',
  [ValidationRule.LatitudeRange]:
    'Die Eingabe muss zwischen 47.000 und 55.900 liegen und 3 bis 14 Nachkommastellen haben.',
  [ValidationRule.LongitudeRange]:
    'Die Eingabe muss zwischen 5.000 und 16.900 liegen und 3 bis 14 Nachkommastellen haben.',
  [ValidationRule.RdTrIdFormat]:
    'Ungültige TR-ID. Sie muss 11 Zeichen lang sein: „D“, gefolgt von 9 Buchstaben und/oder Zahlen sowie einer weiteren Zahl.',
  [ValidationRule.RdTrIdCheckDigit]: 'Die TR-ID ist fehlerhaft.',
  [ValidationRule.RdSrIdFormat]:
    'Ungültige SR-ID. Sie muss 11 Zeichen lang sein: „C“, gefolgt von 9 Buchstaben und/oder Zahlen sowie einer weiteren Zahl.',
  [ValidationRule.RdSrIdCheckDigit]: 'Die SR-ID ist fehlerhaft.',
  [ValidationRule.TimeFormat]: 'Ungültiges Zeitformat (Format: HH:MM, von 00:00 bis 23:59)',
  [ValidationRule.IbanFormat]: 'Ungültiges IBAN-Format',
  [ValidationRule.MarketLocationIdFormat]: 'Die Marktlokations-ID muss eine 11-stellige Zahl sein.',
  [ValidationRule.LeiFormat]: 'Die Eingabe muss genau 20 Zeichen lang sein.',
  [ValidationRule.RegistrationNumberFormat]:
    'Die Registernummer besteht aus Ziffern und optional 1–2 Buchstaben am Ende.',
  [ValidationRule.SmallDvTotalInstalledCapacityMinimum]: (p) =>
    `Die gesamte installierte Leistung muss mindestens ${p['minimumKw']} kW(p) betragen.`,
  [ValidationRule.SmallDvTotalInstalledCapacityRequiresOwnConsumption]: (p) =>
    `Anlagen über ${p['maximumKw']} kW(p) können nur mit Eigenverbrauch über diesen Prozess angeboten werden.`,
  [ValidationRule.SmallDvTotalInstalledCapacityMaximum]: (p) =>
    `Anlagen über ${p['maximumKw']} kW(p) können nicht über diesen Prozess angeboten werden.`,
  [ValidationRule.RemoteControlHardwarePartner]: 'Bitte wählen Sie ServiceZeit / IntegraSun oder ME2C (WAGO Partner).',
  [ValidationRule.KycApproved]: 'Das Ergebnis der KYC-Prüfung muss auf „Freigegeben" gesetzt sein.',
}

export const resolveRuleMessage = (rule: ValidationRule, params: Record<string, unknown> = {}): string => {
  const entry = RULE_MESSAGES[rule]
  return typeof entry === 'function' ? entry(params) : entry
}

export const initializeGlobalErrorMessages = () => {
  z.config({
    customError: (iss) => {
      if (iss.code === 'invalid_type') {
        if (iss.expected === 'int') {
          return 'Bitte geben Sie eine ganze Zahl ein (keine Dezimalstellen)'
        }
        return EMPTY_FIELD_ERROR
      }
      if (iss.code === 'too_small') {
        return `Eingabe muss größer als ${iss.minimum} sein`
      }
      if (iss.code === 'too_big') {
        return `Eingabe muss kleiner als ${iss.maximum} sein`
      }
      if (iss.code === 'invalid_format') {
        if (iss.format === 'email') {
          return 'Ungültige E-Mail-Adresse'
        }
      }
      if (iss.code === 'invalid_value') {
        return EMPTY_FIELD_ERROR
      }
      return customErrorMessage(iss as z.core.$ZodIssue) ?? 'Unerwarteter Fehler'
    },
  })
}

const customErrorMessage = (iss: z.core.$ZodIssue): string | undefined => {
  if (iss.code !== 'custom') return undefined
  const rule = iss.params?.['rule'] as ValidationRule | undefined
  if (rule && rule in RULE_MESSAGES) {
    return resolveRuleMessage(rule, iss.params ?? {})
  }
  return undefined
}
